// src/pages/ci/ListCommercialClients.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Building2, Phone, Mail, Calendar, DollarSign,
  User, MapPin, Zap, Plus, Search, Filter,
  Eye, Edit, Trash2, X, Save, Clock
} from 'lucide-react';

// ── Design tokens ────────────────────────────────────────────────
const T = {
  bg:       '#080C18',
  surface:  '#0D1120',
  surface2: '#111827',
  border:   'rgba(255,255,255,0.07)',
  border2:  'rgba(255,255,255,0.10)',
  text:     '#E2E8F0',
  muted:    'rgba(255,255,255,0.35)',
  faint:    'rgba(255,255,255,0.12)',
  accent:   'linear-gradient(135deg,#3B82F6,#22D3A5)',
  blue:     '#3B82F6',
  teal:     '#22D3A5',
  font:     "'DM Sans', sans-serif",
  mono:     "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes modalIn { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }

  .lc-row { transition: background 0.15s; }
  .lc-row:hover { background: rgba(255,255,255,0.03) !important; }

  .lc-input {
    width: 100%; padding: 10px 12px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 10px; color: #E2E8F0;
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; box-sizing: border-box; transition: border-color 0.15s, background 0.15s;
  }
  .lc-input:focus {
    background: rgba(255,255,255,0.08);
    border-color: rgba(96,165,250,0.5);
    box-shadow: 0 0 0 3px rgba(96,165,250,0.10);
  }
  .lc-input::placeholder { color: rgba(255,255,255,0.2); }
  .lc-input-icon { position: relative; }
  .lc-input-icon svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; }
  .lc-input-icon .lc-input { padding-left: 38px; }

  .lc-select {
    width: 100%; padding: 10px 12px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 10px; color: #E2E8F0;
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; cursor: pointer; box-sizing: border-box;
    -webkit-appearance: none; appearance: none;
  }
  .lc-select option { background: #1E2535; color: #E2E8F0; }

  .lc-textarea {
    width: 100%; padding: 10px 12px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 10px; color: #E2E8F0;
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; resize: vertical; box-sizing: border-box;
    transition: border-color 0.15s;
  }
  .lc-textarea:focus { border-color: rgba(96,165,250,0.5); box-shadow: 0 0 0 3px rgba(96,165,250,0.10); }

  .lc-label {
    display: block; font-size: 10px; font-weight: 700;
    letter-spacing: 0.10em; text-transform: uppercase;
    color: rgba(255,255,255,0.35); margin-bottom: 6px;
  }

  .lc-btn-primary {
    padding: 10px 20px; border: none; border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color: #fff;
    display: flex; align-items: center; gap: 7px;
    box-shadow: 0 4px 14px rgba(59,130,246,0.3); transition: opacity 0.15s, transform 0.15s;
  }
  .lc-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }

  .lc-btn-ghost {
    padding: 10px 20px; border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
    background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.6);
    display: flex; align-items: center; gap: 7px; transition: background 0.15s;
  }
  .lc-btn-ghost:hover { background: rgba(255,255,255,0.08); color: #E2E8F0; }

  .lc-action-btn {
    width: 34px; height: 34px; border-radius: 9px; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center; transition: all 0.15s;
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 4px; }
`;

// ── Company Logo ─────────────────────────────────────────────────
function CompanyLogo({ name, domain, size = 40 }) {
  const [logoError, setLogoError] = useState(false);
  const [loading, setLoading] = useState(true);

  const gradients = [
    ['#667eea','#764ba2'],['#4facfe','#00f2fe'],['#43e97b','#38f9d7'],
    ['#fa709a','#fee140'],['#30cfd0','#667eea'],['#f093fb','#f5576c'],
    ['#ff6e7f','#bfe9ff'],['#a18cd1','#fbc2eb'],['#fccb90','#d57eeb'],['#a1c4fd','#c2e9fb']
  ];
  const hash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const [c1, c2] = gradients[hash % gradients.length];
  const initials = name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  const logoUrl = domain ? `https://logo.clearbit.com/${domain}?size=${size * 2}` : null;

  if (!logoUrl || logoError) {
    return (
      <div style={{
        width: size, height: size, minWidth: size, borderRadius: 10, flexShrink: 0,
        background: `linear-gradient(135deg,${c1},${c2})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 700, fontSize: size * 0.34,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)', letterSpacing: '0.5px',
        fontFamily: T.font,
      }}>{initials}</div>
    );
  }

  return (
    <div style={{ position: 'relative', width: size, height: size, minWidth: size, flexShrink: 0 }}>
      {loading && (
        <div style={{
          width: size, height: size, borderRadius: 10, position: 'absolute',
          background: `linear-gradient(135deg,${c1},${c2})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontWeight: 700, fontSize: size * 0.34,
        }}>{initials}</div>
      )}
      <img
        src={logoUrl} alt={`${name} logo`}
        style={{ width: size, height: size, borderRadius: 10, objectFit: 'cover', display: loading ? 'none' : 'block', border: '1px solid rgba(255,255,255,0.10)' }}
        onLoad={() => setLoading(false)}
        onError={() => { setLogoError(true); setLoading(false); }}
      />
    </div>
  );
}

// ── Badges ───────────────────────────────────────────────────────
const ContractBadge = ({ type }) => {
  const cfg = type === 'industrial'
    ? { bg: 'rgba(251,191,36,0.12)', color: '#FBBF24', border: 'rgba(251,191,36,0.25)' }
    : { bg: 'rgba(59,130,246,0.12)', color: '#60A5FA', border: 'rgba(59,130,246,0.25)' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: T.mono,
    }}>{type}</span>
  );
};

const BillingBadge = ({ type }) => {
  const cfg = type === 'monthly'
    ? { bg: 'rgba(34,211,165,0.10)', color: '#22D3A5', border: 'rgba(34,211,165,0.22)' }
    : { bg: 'rgba(167,139,250,0.10)', color: '#A78BFA', border: 'rgba(167,139,250,0.22)' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      padding: '3px 9px', borderRadius: 6, fontSize: 10, fontWeight: 600, fontFamily: T.mono,
    }}>{type === 'monthly' ? 'Monthly' : 'Per Service'}</span>
  );
};

// ── Main Component ───────────────────────────────────────────────
const ListCiClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    client_name: "", contact_person: "", contact_number: "", email: "",
    address: "", contract_type: "commercial", contract_start_date: "",
    total_system_size: "", sales_price_before_tax: "", total_man_hours: "",
    sales_agent: "", billing_type: "monthly", contractual_services_count: ""
  });

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients`);
      if (response.data.success) setClients(response.data.data.clients);
      else setError("Failed to load clients");
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError("Failed to load clients. Please try again.");
    } finally { setLoading(false); }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await axios.patch(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients/${id}/deactivate`);
        alert("Client deactivated successfully!");
        fetchClients();
      } catch (err) { console.error("Error deleting client:", err); alert("Failed to delete client"); }
    }
  };

  const handleEditClick = (client) => {
    setEditingClient(client);
    setFormData({
      client_name: client.client_name || "",
      contact_person: client.contact_person || "",
      contact_number: client.contact_number || "",
      email: client.email || "",
      address: client.address || "",
      contract_type: client.contract_type || "commercial",
      contract_start_date: client.contract_start_date ? client.contract_start_date.split('T')[0] : "",
      total_system_size: client.total_system_size || "",
      sales_price_before_tax: client.sales_price_before_tax || "",
      total_man_hours: client.total_man_hours || "",
      sales_agent: client.sales_agent || "",
      billing_type: client.billing_type || "monthly",
      contractual_services_count: client.contractual_services_count || ""
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingClient(null);
    setFormData({
      client_name: "", contact_person: "", contact_number: "", email: "",
      address: "", contract_type: "commercial", contract_start_date: "",
      total_system_size: "", sales_price_before_tax: "", total_man_hours: "",
      sales_agent: "", billing_type: "monthly", contractual_services_count: ""
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name || !formData.contact_person || !formData.contact_number) {
      alert("Please fill in all required fields"); return;
    }
    try {
      setSaving(true);
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/commercial-clients/${editingClient.id}`, formData
      );
      if (response.data.success) { alert("Client updated successfully!"); handleCloseModal(); fetchClients(); }
      else alert("Failed to update client");
    } catch (err) { console.error("Error updating client:", err); alert("Failed to update client. Please try again."); }
    finally { setSaving(false); }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch =
      client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.sales_agent.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === "all" || client.contract_type === filterType;
    return matchesSearch && matchesFilter;
  });

  // ── Shared cell style ──
  const th = { padding: '13px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.12em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
    fontFamily: T.mono, whiteSpace: 'nowrap', borderBottom: `1px solid ${T.border}`,
    background: 'rgba(255,255,255,0.02)' };
  const td = { padding: '14px 16px', borderBottom: `1px solid rgba(255,255,255,0.04)`, verticalAlign: 'middle' };

  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px' }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}` }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · C&I MANAGEMENT</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1 }}>C&I Clients</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Commercial & Industrial Solar Projects</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1, fontFamily: T.mono }}>{clients.length}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Active Clients</div>
            </div>
            <button className="lc-btn-primary" onClick={() => navigate('/commercial/add-clients')}>
              <Plus size={16} /> Add Client
            </button>
          </div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* ── SEARCH + FILTER ── */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
        padding: '14px 18px', marginBottom: 16,
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="lc-input-icon" style={{ flex: 1, minWidth: 220 }}>
          <Search size={15} color="rgba(255,255,255,0.25)" />
          <input
            type="text"
            className="lc-input"
            placeholder="Search by name, contact, or sales agent…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={14} color={T.muted} />
          <select className="lc-select" style={{ width: 150 }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="commercial">Commercial</option>
            <option value="industrial">Industrial</option>
          </select>
        </div>
        <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono, marginLeft: 'auto' }}>
          {filteredClients.length} / {clients.length} clients
        </div>
      </div>

      {/* ── TABLE CARD ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{
              width: 40, height: 40, border: `3px solid ${T.border}`,
              borderTop: `3px solid ${T.blue}`, borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto'
            }} />
            <p style={{ marginTop: 14, color: T.muted, fontSize: 13 }}>Loading clients…</p>
          </div>
        ) : error ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <p style={{ color: '#F87171', fontWeight: 600, marginBottom: 14 }}>{error}</p>
            <button className="lc-btn-primary" onClick={fetchClients} style={{ margin: '0 auto' }}>Try Again</button>
          </div>
        ) : filteredClients.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>📋</div>
            <p style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6 }}>No clients found</p>
            <p style={{ color: T.muted, fontSize: 13 }}>
              {searchTerm ? 'Try adjusting your search criteria' : 'Add your first client to get started'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 40 }}>#</th>
                  <th style={th}>Client</th>
                  <th style={th}>Contact Info</th>
                  <th style={th}>Contract</th>
                  <th style={th}>System Size</th>
                  <th style={th}>Man Hours</th>
                  <th style={th}>Billing</th>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client, index) => (
                  <tr key={client.id} className="lc-row">

                    {/* # */}
                    <td style={td}>
                      <span style={{ color: T.muted, fontSize: 11, fontFamily: T.mono }}>{index + 1}</span>
                    </td>

                    {/* Client */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <CompanyLogo name={client.client_name} domain={client.email?.split('@')[1]} size={40} />
                        <div>
                          <div style={{ fontWeight: 700, color: T.text, fontSize: 14, marginBottom: 3 }}>
                            {client.client_name}
                          </div>
                          <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <User size={10} /> {client.sales_agent}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td style={td}>
                      <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 3 }}>
                        {client.contact_person}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Phone size={10} /> {client.contact_number}
                      </div>
                      {client.email && (
                        <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                          <Mail size={10} /> {client.email}
                        </div>
                      )}
                    </td>

                    {/* Contract */}
                    <td style={td}>
                      <ContractBadge type={client.contract_type} />
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={10} />
                        {new Date(client.contract_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </td>

                    {/* System Size */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Zap size={13} color="#FBBF24" />
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.mono }}>
                          {parseFloat(client.total_system_size).toLocaleString()}
                        </span>
                        <span style={{ fontSize: 10, color: T.muted }}>kW</span>
                      </div>
                    </td>

                    {/* Man Hours */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Clock size={12} color={T.muted} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.mono }}>
                          {client.total_man_hours || 0}
                        </span>
                        <span style={{ fontSize: 10, color: T.muted }}>hrs</span>
                      </div>
                    </td>

                    {/* Billing */}
                    <td style={td}>
                      <BillingBadge type={client.billing_type} />
                      {client.billing_type === 'per_service_count' && client.contractual_services_count && (
                        <div style={{ fontSize: 10, color: T.muted, marginTop: 4, fontFamily: T.mono }}>
                          {client.contractual_services_count} services
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="lc-action-btn"
                          onClick={() => handleEditClick(client)}
                          title="Edit Client"
                          style={{ background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.22)', color: '#FBBF24' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.22)'; e.currentTarget.style.transform = 'scale(1.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.10)'; e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="lc-action-btn"
                          onClick={() => handleDelete(client.id, client.client_name)}
                          title="Delete Client"
                          style={{ background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.22)', color: '#F87171' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.22)'; e.currentTarget.style.transform = 'scale(1.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.10)'; e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── EDIT MODAL ── */}
      {showEditModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 10000, padding: 20,
        }}>
          <div style={{
            background: T.surface, border: `1px solid ${T.border2}`, borderRadius: 20,
            maxWidth: 860, width: '100%', maxHeight: '90vh', overflow: 'auto',
            boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
            animation: 'modalIn 0.2s ease both',
          }}>

            {/* Modal Header */}
            <div style={{
              padding: '20px 24px', borderBottom: `1px solid ${T.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'rgba(255,255,255,0.02)',
            }}>
              <div>
                <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4 }}>C&I MANAGEMENT</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>Edit Client</div>
              </div>
              <button
                onClick={handleCloseModal}
                style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.border}`, color: T.muted, width: 34, height: 34, borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />

            {/* Modal Body */}
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>

              {/* ── Section: Client Info ── */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Building2 size={14} color="#60A5FA" />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Client Information</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                  {[
                    { label: 'Client Name', name: 'client_name', type: 'text', req: true },
                    { label: 'Sales Agent', name: 'sales_agent', type: 'text', req: true },
                    { label: 'Contract Start Date', name: 'contract_start_date', type: 'date', req: true },
                  ].map(f => (
                    <div key={f.name}>
                      <label className="lc-label">{f.label}{f.req && <span style={{ color: '#F87171' }}> *</span>}</label>
                      <input type={f.type} name={f.name} value={formData[f.name]} onChange={handleChange} required={f.req} className="lc-input" />
                    </div>
                  ))}
                  <div>
                    <label className="lc-label">Contract Type <span style={{ color: '#F87171' }}>*</span></label>
                    <select name="contract_type" value={formData.contract_type} onChange={handleChange} required className="lc-select">
                      <option value="commercial">Commercial</option>
                      <option value="industrial">Industrial</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 24 }} />

              {/* ── Section: Contact ── */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <User size={14} color="#22D3A5" />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Contact Information</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                  {[
                    { label: 'Contact Person', name: 'contact_person', type: 'text', req: true },
                    { label: 'Contact Number', name: 'contact_number', type: 'tel', req: true },
                    { label: 'Email', name: 'email', type: 'email', req: false },
                  ].map(f => (
                    <div key={f.name}>
                      <label className="lc-label">{f.label}{f.req && <span style={{ color: '#F87171' }}> *</span>}</label>
                      <input type={f.type} name={f.name} value={formData[f.name]} onChange={handleChange} required={f.req} className="lc-input" />
                    </div>
                  ))}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="lc-label">Address</label>
                    <textarea name="address" value={formData.address} onChange={handleChange} rows={2} className="lc-textarea" />
                  </div>
                </div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 24 }} />

              {/* ── Section: System & Pricing ── */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Zap size={14} color="#FBBF24" />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>System & Pricing</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                  {[
                    { label: 'Total System Size (kW)', name: 'total_system_size', step: '0.01' },
                    { label: 'Sales Price (Before Tax)', name: 'sales_price_before_tax', step: '0.01' },
                    { label: 'Total Man Hours', name: 'total_man_hours', step: '1' },
                  ].map(f => (
                    <div key={f.name}>
                      <label className="lc-label">{f.label} <span style={{ color: '#F87171' }}>*</span></label>
                      <input type="number" step={f.step} name={f.name} value={formData[f.name]} onChange={handleChange} required className="lc-input" />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 24 }} />

              {/* ── Section: Billing ── */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DollarSign size={14} color="#A78BFA" />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Billing Configuration</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                  <div>
                    <label className="lc-label">Billing Type <span style={{ color: '#F87171' }}>*</span></label>
                    <select name="billing_type" value={formData.billing_type} onChange={handleChange} required className="lc-select">
                      <option value="monthly">Monthly</option>
                      <option value="per_service_count">Per Service Count</option>
                    </select>
                  </div>
                  {formData.billing_type === 'per_service_count' && (
                    <div>
                      <label className="lc-label">Contractual Services Count <span style={{ color: '#F87171' }}>*</span></label>
                      <input type="number" name="contractual_services_count" value={formData.contractual_services_count} onChange={handleChange} required className="lc-input" />
                    </div>
                  )}
                </div>
              </div>

              {/* ── Actions ── */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
                <button type="button" className="lc-btn-ghost" onClick={handleCloseModal}>
                  <X size={15} /> Cancel
                </button>
                <button type="submit" className="lc-btn-primary" disabled={saving} style={{ opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  <Save size={15} /> {saving ? 'Updating…' : 'Update Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListCiClients;